import { useMemo } from "react";
import { useGetList } from "ra-core";

import type { Contact } from "../types";
import { leadSlaFilterValues } from "./contactModel";

/** Stable inbox filter — expanded to contact ids in the dataProvider via the
 * unread_whatsapp_contact_ids RPC (see 02_functions.sql). */
export const NEEDS_WHATSAPP_REPLY_FILTER = {
  desk_queue: "needs_whatsapp_reply",
} as const;

/**
 * Single shared React Query path for "which contacts need a WhatsApp reply".
 * Used by ContactListFilter (toggle button), ContactListContent (row badges),
 * the Dashboard "Needs attention" strip, and the Header badge.
 */
export const useNeedsWhatsAppReply = () => {
  const { data: contacts, total } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "last_seen", order: "DESC" },
    filter: NEEDS_WHATSAPP_REPLY_FILTER,
  });

  return useMemo(() => {
    const contactIds = new Set((contacts ?? []).map((c) => String(c.id)));
    return {
      filter: { ...NEEDS_WHATSAPP_REPLY_FILTER },
      contactIds,
      count: total ?? contactIds.size,
    };
  }, [contacts, total]);
};

export type DeskAttentionCounts = {
  unassigned: number;
  slaOverdue: number;
  stale: number;
  needsWhatsAppReply: number;
  isPending: boolean;
};

/**
 * Desk-queue counts for the Dashboard "Needs attention" strip and the Header
 * Inbox badge (`needsWhatsAppReply` via unread_whatsapp_contact_ids RPC).
 * Reuses the same `desk_queue` filter expansion as the Contacts list
 * (RLS-scoped: managers see the whole desk, agents see their own + unassigned).
 */
export const useDeskAttentionCounts = (): DeskAttentionCounts => {
  const deskFilters = leadSlaFilterValues();

  const { total: unassigned, isPending: pendingUnassigned } = useGetList(
    "contacts",
    {
      pagination: { page: 1, perPage: 1 },
      filter: deskFilters.unassigned,
    },
  );
  const { total: slaOverdue, isPending: pendingSla } = useGetList("contacts", {
    pagination: { page: 1, perPage: 1 },
    filter: deskFilters.slaOverdue,
  });
  const { total: stale, isPending: pendingStale } = useGetList("contacts", {
    pagination: { page: 1, perPage: 1 },
    filter: deskFilters.stale,
  });
  const { count: needsWhatsAppReply, contactIds } = useNeedsWhatsAppReply();

  return {
    unassigned: unassigned ?? 0,
    slaOverdue: slaOverdue ?? 0,
    stale: stale ?? 0,
    needsWhatsAppReply: needsWhatsAppReply ?? contactIds.size,
    isPending: pendingUnassigned || pendingSla || pendingStale,
  };
};
